/*
 * @Author: Ryan
 * @Date: 2026-01-09
 * @Description: Explore Our AI Tools section with grid layout and carousel
 */
import Image from 'next/image';
import Link from 'next/link';
import React, { useRef, useState } from 'react';

import {
  avatarVideoProURL,
  videoGenerateURL,
  videoTranslateURL,
} from '@/utils/routeConstants';

import styles from './index.module.scss';

interface Tool {
  id: string;
  imageUrl: string;
  title: string;
  description: string;
  link: string;
}

const TOOLS: Tool[] = [
  {
    id: 'video-translator',
    imageUrl: '/images/explore-ai-video-translator.png',
    title: 'AI Video Translator',
    description:
      'AI Video Translator – Instantly translate your videos into 30+ languages with accurate subtitles, natural AI dubbing, and seamless lip sync, making your content accessible to global audiences across social media, e-learning, and marketing.',
    link: videoTranslateURL,
  },
  {
    id: 'video-generator',
    imageUrl: '/images/explore-ai-video-generator.png',
    title: 'AI Video Generator',
    description:
      'Generate videos from text or images in minutes. Turn scripts, ideas, or visuals into engaging videos with AI-powered scenes, motion, and visuals—no filming or editing required. Perfect for marketing videos, social content, product showcases, and storytelling at scale.',
    link: videoGenerateURL,
  },
  {
    id: 'ai-avatar',
    imageUrl: '/images/explore-ai-avatar-generator.png',
    title: 'AI Avatar Generator',
    description:
      'Create lifelike digital avatars in minutes. Perfect for presentations, marketing, and social media—our AI avatar generator brings your message to life with realistic expressions and gestures.',
    link: avatarVideoProURL,
  },
];

interface ToolCardProps {
  tool: Tool;
}

const ToolCard: React.FC<ToolCardProps> = ({ tool }) => (
  <Link href={tool.link} className={styles.card}>
    {/* 工具图片 */}
    <div className={styles.imageWrapper}>
      <Image
        src={tool.imageUrl}
        alt={tool.title}
        fill
        className={styles.image}
        loading="lazy"
      />
    </div>

    {/* 工具内容 */}
    <div className={styles.cardContent}>
      <h3 className={styles.cardTitle}>{tool.title}</h3>
      <p className={styles.cardDescription}>{tool.description}</p>
    </div>
  </Link>
);

const ExploreToolsSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  // 处理移动端滚动
  const handleScroll = () => {
    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const itemWidth = carouselRef.current.scrollWidth / TOOLS.length;
      const currentIndex = Math.round(scrollLeft / itemWidth);
      if (currentIndex !== activeIndex) {
        setActiveIndex(currentIndex);
      }
    }
  };

  // 处理指示器点击
  const handleIndicatorClick = (index: number) => {
    setActiveIndex(index);
    if (carouselRef.current) {
      const itemWidth = carouselRef.current.scrollWidth / TOOLS.length;
      carouselRef.current.scrollTo({
        left: itemWidth * index,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        {/* 标题区域 */}
        <h2 className={styles.title}>Explore Our AI Tools</h2>

        {/* PC/Pad 端：3 列网格布局 */}
        <div className={styles.grid}>
          {TOOLS.map((tool) => (
            <ToolCard key={tool.id} tool={tool} />
          ))}
        </div>

        {/* 移动端：轮播 */}
        <div className={styles.carousel}>
          {/* 轮播容器 */}
          <div ref={carouselRef} onScroll={handleScroll} className={styles.carouselTrack}>
            {TOOLS.map((tool) => (
              <div key={tool.id} className={styles.carouselItem}>
                <ToolCard tool={tool} />
              </div>
            ))}
          </div>

          {/* 指示器 */}
          <div className={styles.indicators}>
            {TOOLS.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleIndicatorClick(index)}
                className={`${styles.indicator} ${activeIndex === index ? styles.active : ''}`}
                aria-label={`Go to tool ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExploreToolsSection;
